import { useDispatch } from "react-redux";

import { MCDANIEL_FOLDER_ID } from "constants/settings.constants";
import { setUserForecastSettings } from "store/features";
import { useForecastStore } from "stores/forecast";

import { getForecastSourceConfig } from "../../../user-settings/constants/userSetting.constants";
import { useEffectiveForecastFolderId } from "../../../user-settings/hooks/useEffectiveForecastFolderId";
import { useUserDefaults } from "../../../user/hooks";

export function useClearForecasts() {
  const dispatch = useDispatch();
  const defaults = useUserDefaults();
  // Same resolution the loader's applier uses, so both writers of forecast selection agree on
  // what a persisted value means. Without this, clearing forecasts re-checks Novi for a user
  // whose org no longer has it, while the applier downgrades — and whichever ran last wins.
  const { effectiveFolderId, isReady } = useEffectiveForecastFolderId(
    defaults?.forecastSourceSetting?.forecastFolderId
  );
  const setUseMcDanielForecasts = useForecastStore((s) => s.setUseMcDanielForecasts);
  const setUseWorkingForecasts = useForecastStore((s) => s.setUseWorkingForecasts);
  const setUseNoviForecasts = useForecastStore((s) => s.setUseNoviForecasts);
  const setCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);
  const setExpandedForecastKeys = useForecastStore((s) => s.setExpandedForecastKeys);
  const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const setCheckedGlobalForecasts = useForecastStore((s) => s.setCheckedGlobalForecasts);

  function clearForecasts() {
    // TODO BF: cache
    // updated this method to respect the user defaults for the forecast source
    // getForecastSourceConfig falls back to McDaniel for an unrecognised id, which is what
    // keeps this path from crashing during login project-init for a user whose saved value
    // the running build does not know.
    //
    // Honour isReady the way the loader's applier does. Project-init can fire this before the
    // Novi availability query resolves, and a Novi-dependent value cannot be read yet: applying
    // it would check Novi for an org that may have lost it, leave the store non-pristine, and
    // the applier would then stand down on its pristine guard — so Novi would stay checked and
    // ride every request payload for the session. Clearing to EVA Research instead leaves the
    // store pristine, so the applier applies the real value the moment availability lands, and
    // it errs toward one source too few rather than one the org may not be entitled to.
    const config = getForecastSourceConfig(
      isReady ? effectiveFolderId : MCDANIEL_FOLDER_ID
    );

    setUseMcDanielForecasts(config.useMcDaniel);
    setUseWorkingForecasts(config.useWorking);
    setUseNoviForecasts(config.useNovi);
    dispatch(
      setUserForecastSettings({
        forecastSourceFolder: null,
        excludeMcDanielForecasts: config.excludeMcDaniel,
        reserveCategory: null,
        excludeNoviForecasts: config.excludeNovi,
        specialForecastFolders: config.specialFolderNames
      })
    );

    setCheckedForecastKeys(config.checkedKeys);
    setExpandedForecastKeys([]);
    setCheckedGlobalTypeWells([]);
    setCheckedForecasts([]);
    setCheckedGlobalForecasts([]);
  }

  return { update: clearForecasts };
}
